import cookie from 'react-cookie';
import { MILLISECONDS_PER_DAY, TODAY } from 'constants/constants';

/**
 * Black Friday promotion helper functions
 */

// Black Friday window (UTC): 2025-11-24 00:00:00 → 2025-12-01 23:59:59
export const BLACK_FRIDAY_START_DATE = new Date('2025-11-24T00:00:00Z');
export const BLACK_FRIDAY_COUNTDOWN_DAYS_BEFORE = 3;
export const BLACK_FRIDAY_END_DATE = new Date('2025-12-01T23:59:59Z');

/**
 * Check if current date is within Black Friday promotion period
 * @returns {boolean}
 */
export const isBlackFridayActive = () => {
  return TODAY >= BLACK_FRIDAY_START_DATE && TODAY <= BLACK_FRIDAY_END_DATE;
};

/**
 * Check if user is from LinkedIn traffic
 * @returns {boolean}
 */
export const isLinkedInUser = () => {
  const utmCampaignCookie = cookie.load('utm_campaign');

  return utmCampaignCookie === 'linkedin_job';
};

/**
 * Check if user is eligible for Black Friday promotion
 * Sellers during Black Friday period (non-LinkedIn OR LinkedIn Variant B)
 * @param {Object} user - User object (required for variant check)
 * @returns {boolean}
 */
export const isBlackFridayEligible = (user = null) => {
  // Must be a seller (not a buyer)
  if (user && user.type === 'buyer') {
    return false;
  }

  // Must be during Black Friday period
  if (!isBlackFridayActive()) {
    return false;
  }

  const isLinkedIn = isLinkedInUser();

  // If not LinkedIn, eligible for Black Friday
  if (!isLinkedIn) {
    return true;
  }

  // If LinkedIn, only Variant B is eligible (Variant A gets WORK100)
  // Variant A = user_id % 2 === 0, Variant B = user_id % 2 === 1
  if (user && user.id) {
    const isVariantA = user.id % 2 === 0;
    return !isVariantA; // Only Variant B (LinkedIn users who don't see WORK100)
  }

  // If we don't have user ID, default to eligible
  return true;
};

/**
 * Get countdown to Black Friday end date (days only for pricing cards)
 * @returns {string} Formatted countdown string (days only)
 */
export const getBlackFridayCountdown = () => {
  const now = new Date();
  const difference = BLACK_FRIDAY_END_DATE - now;

  if (difference <= 0) {
    return 'Offer ended';
  }

  if (difference > BLACK_FRIDAY_COUNTDOWN_DAYS_BEFORE * MILLISECONDS_PER_DAY) {
    return '';
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));

  return `${days} ${days === 1 ? 'day' : 'days'} left`;
};

/**
 * Get full countdown to Black Friday end date (for banner)
 * @returns {string} Formatted countdown string with days, hours, minutes, seconds
 */
export const getBlackFridayCountdownFull = () => {
  const now = new Date();
  const difference = BLACK_FRIDAY_END_DATE - now;

  if (difference <= 0) {
    return '- Offer ended';
  }

  if (difference > BLACK_FRIDAY_COUNTDOWN_DAYS_BEFORE * MILLISECONDS_PER_DAY) {
    return '- Limited time';
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((difference / 1000 / 60) % 60);
  const seconds = Math.floor((difference / 1000) % 60);

  if (days > 0) {
    return `⏰ ${days}d ${hours}h ${minutes}m ${seconds}s left!`;
  } else if (hours > 0) {
    return `⏰ ${hours}h ${minutes}m ${seconds}s left!`;
  } else {
    return `⏰ ${minutes}m ${seconds}s left!`;
  }
};
